// Global Instructions Rule Applied!
//
// Configuration → Statement Types admin model
// (docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md i.5).
//
// Shape mirrors one row of public.view__investor_statement_types_admin: a live
// cfg.investor_statement_types row, its document group, the registry name of
// the report it prints and the reports it may print instead. The run finds
// each type by its code and the deck by its scope, so the panel is update
// only - there is no create or delete anywhere in this model.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

/** A report that fits the type's scope: an active cfg.document_reports row
 *  carrying every parameter the run passes. Built by stmt__type_report_fits,
 *  so the select offers exactly what the save accepts. */
export const StatementTypeReportOptionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
    description: z.string().nullable()
  })
  .strict()

export type StatementTypeReportOption = z.infer<typeof StatementTypeReportOptionSchema>

export const StatementTypeAdminRowSchema = z
  .object({
    // integer identities: they differ between environments
    investor_statement_types__id: z.number().int(),
    // Immutable: the run finds each type by its code.
    investor_statement_types__code: z.string(),
    investor_statement_types__label: z.string(),
    // NULL when blank; readers fall back to the label
    investor_statement_types__short_label: z.string().nullable(),
    // entity | investor | account - a CHECK on the table, never an enum here
    investor_statement_types__scope: z.string(),
    investor_statement_types__display_order: z.number().int().nullable(),
    investor_statement_types__document_report_code: z.string(),
    investor_statement_types__date_modified: z.string().nullable(),
    // audit.resolve_user_name: System for a NULL modified_by
    prog__modified_by_name: z.string(),
    statement_document_groups__id: z.number().int().nullable(),
    statement_document_groups__code: z.string().nullable(),
    statement_document_groups__label: z.string().nullable(),
    // NULL for the two statement codes, which have no registry row
    document_reports__name: z.string().nullable(),
    prog__scope_label: z.string(),
    prog__report_editable: z.boolean(),
    // NULL for a statement code; false = every document of the type fails at
    // generation until another report is chosen
    prog__report_is_active: z.boolean().nullable(),
    prog__report_options: z.array(StatementTypeReportOptionSchema)
  })
  .strict()

export type StatementTypeAdminRow = z.infer<typeof StatementTypeAdminRowSchema>

export function validateStatementTypeAdminRow(data: unknown, options: { isArray: true }): StatementTypeAdminRow[]
export function validateStatementTypeAdminRow(data: unknown, options?: { isArray?: false }): StatementTypeAdminRow
export function validateStatementTypeAdminRow(
  data: unknown,
  options: { isArray?: boolean } = {}
): StatementTypeAdminRow | StatementTypeAdminRow[] {
  try {
    return options.isArray
      ? validateWithZodSchema(StatementTypeAdminRowSchema, data, 'statement type', { isArray: true })
      : validateWithZodSchema(StatementTypeAdminRowSchema, data, 'statement type', { isArray: false })
  } catch (e) {
    throw new Error(`Statement type validation failed: ${e}`)
  }
}
